import React from 'react';

/**
 * Tracker #57 — tenant translation policy. Translation is entitled on
 * Standard and Enterprise (Audit.TranslationMetered) and metered on
 * translation.characters; this card is the customer admin's lever: switch
 * the surface off, set the monthly character cap, and decide whether use
 * beyond the cap bills as overage or blocks until the next period.
 */
type TranslationConfig = {
  enabled: boolean;
  includedCharacters: number;
  allowOverage: boolean;
};

type TranslationUsage = {
  billingPeriodId: string;
  usedCharactersThisPeriod: number;
  includedCharacters: number;
  remainingCharacters: number;
  allowOverage: boolean;
  planIncludedCharacters: number;
};

type TranslationPolicyPayload = {
  plan?: string;
  entitled?: boolean;
  available?: boolean;
  providerConfigured?: boolean;
  config?: Partial<TranslationConfig>;
  supportedLanguages?: Array<{ code: string; label: string }>;
  usage?: TranslationUsage | null;
};

interface TranslationPolicyCardProps {
  selectedTenantId: string;
  canEdit: boolean;
}

const defaultConfig: TranslationConfig = { enabled: true, includedCharacters: 0, allowOverage: false };

const formatCharacters = (value: number): string => new Intl.NumberFormat('en-GB').format(Math.max(0, Math.floor(value || 0)));

const TranslationPolicyCard = ({ selectedTenantId, canEdit }: TranslationPolicyCardProps): JSX.Element => {
  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [saved, setSaved] = React.useState<string | null>(null);
  const [payload, setPayload] = React.useState<TranslationPolicyPayload | null>(null);
  const [config, setConfig] = React.useState<TranslationConfig>(defaultConfig);

  const applyPayload = React.useCallback((next: TranslationPolicyPayload): void => {
    setPayload(next);
    setConfig({ ...defaultConfig, ...(next.config ?? {}) });
  }, []);

  const load = React.useCallback(async (): Promise<void> => {
    if (!selectedTenantId) {
      return;
    }
    setLoading(true);
    setError(null);
    setSaved(null);
    try {
      const response = await fetch(`/api/admin/translation?tenantId=${encodeURIComponent(selectedTenantId)}`);
      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(body?.message || body?.error || `Failed to load translation policy (${response.status})`);
      }
      applyPayload(body as TranslationPolicyPayload);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [applyPayload, selectedTenantId]);

  React.useEffect(() => {
    void load();
  }, [load]);

  const save = async (): Promise<void> => {
    if (!selectedTenantId) {
      return;
    }
    setSaving(true);
    setError(null);
    setSaved(null);
    try {
      const response = await fetch('/api/admin/translation', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tenantId: selectedTenantId, config })
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(body?.message || body?.error || `Failed to save translation policy (${response.status})`);
      }
      applyPayload(body as TranslationPolicyPayload);
      setSaved('Translation policy saved.');
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const usage = payload?.usage ?? null;
  const languages = payload?.supportedLanguages ?? [];

  return (
    <div className="card" style={{ marginTop: '12px' }} data-testid="translation-policy-card">
      <h3>Translation</h3>
      <p className="muted">
        Auditors can translate Audit, Finding and Action text and supplier thread messages into {languages.length ? languages.map((language) => language.label).join(', ') : 'the supported languages'}.
        Translations are stored beside the original and never replace it. Usage is metered per source character under the plan&apos;s monthly inclusion.
      </p>
      {loading && <div className="muted">Loading translation policy…</div>}
      {error && <div className="alert danger" role="alert">{error}</div>}
      {saved && <div className="alert success" role="status">{saved}</div>}
      {payload && (
        <div style={{ display: 'grid', gap: '12px' }}>
          <div className="muted">
            Plan: <strong>{payload.plan ?? 'unknown'}</strong>
            {' · '}Entitled: <strong>{payload.entitled ? 'yes' : 'no'}</strong>
            {' · '}Provider configured: <strong>{payload.providerConfigured ? 'yes' : 'no'}</strong>
            {' · '}Available to users now: <strong>{payload.available ? 'yes' : 'no'}</strong>
          </div>
          {usage && (
            <div className="muted">
              Period {usage.billingPeriodId}: {formatCharacters(usage.usedCharactersThisPeriod)} of {formatCharacters(usage.includedCharacters)} characters used
              {' · '}{formatCharacters(usage.remainingCharacters)} remaining
              {' · '}plan inclusion {formatCharacters(usage.planIncludedCharacters)}
            </div>
          )}
          <label style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <input
              type="checkbox"
              checked={config.enabled}
              disabled={!canEdit || saving}
              onChange={(event) => setConfig((prev) => ({ ...prev, enabled: event.target.checked }))}
            />
            <span>Enable translation for this tenant</span>
          </label>
          <label style={{ display: 'grid', gap: '4px', maxWidth: '320px' }}>
            <span>Monthly character cap</span>
            <input
              className="input"
              type="number"
              min={0}
              step={1000}
              value={config.includedCharacters}
              disabled={!canEdit || saving}
              onChange={(event) => setConfig((prev) => ({ ...prev, includedCharacters: Math.max(0, Math.floor(Number(event.target.value) || 0)) }))}
            />
            <span className="muted">Defaults to the plan inclusion. Lower it to hold spend below the included amount.</span>
          </label>
          <label style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
            <input
              type="checkbox"
              style={{ marginTop: '4px' }}
              checked={config.allowOverage}
              disabled={!canEdit || saving}
              onChange={(event) => setConfig((prev) => ({ ...prev, allowOverage: event.target.checked }))}
            />
            <span>
              <strong>Allow overage</strong>
              <div className="muted" style={{ marginTop: '2px' }}>
                When off, translation stops with a clear message once the cap is reached and resumes next period. When on, use beyond the plan inclusion bills through the Marketplace meter.
              </div>
            </span>
          </label>
          <div>
            <button className="btn" type="button" onClick={() => { void save(); }} disabled={!canEdit || saving || loading}>
              {saving ? 'Saving…' : 'Save translation policy'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TranslationPolicyCard;
